import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { assetService, CoverageCalendarEvent } from '../services/assetService';
import { getErrorMessage } from '../utils/errors';
import PageTopBar from '../components/ui/PageTopBar';
import Card from '../components/ui/Card';

// Cap events shown per day so cells keep an even height; the rest collapse
// into a non-interactive "+N more" count.
const MAX_VISIBLE_EVENTS = 3;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Zero-padded YYYY-MM-DD from a year/month(0-based)/day, all local. */
const toIsoDate = (year: number, month: number, day: number): string =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const ChevronLeft = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const Calendar: React.FC = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-based
  const [events, setEvents] = useState<CoverageCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    const firstDay = toIsoDate(year, month, 1);
    const lastDayNum = new Date(year, month + 1, 0).getDate();
    const lastDay = toIsoDate(year, month, lastDayNum);
    try {
      const data = await assetService.getCalendar(firstDay, lastDay);
      setEvents(data);
    } catch (err: unknown) {
      console.error('Failed to load calendar events:', err);
      setError(getErrorMessage(err, 'Failed to load calendar events'));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Group events by their date string for O(1) per-cell lookup.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CoverageCalendarEvent[]>();
    for (const ev of events) {
      const list = map.get(ev.date);
      if (list) list.push(ev);
      else map.set(ev.date, [ev]);
    }
    return map;
  }, [events]);

  // Build the grid: leading blanks for the first weekday, then the days.
  const cells = useMemo(() => {
    const firstWeekday = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: (number | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(d);
    while (result.length % 7 !== 0) result.push(null);
    return result;
  }, [year, month]);

  const goPrev = () => {
    if (month === 0) {
      setYear(y => y - 1);
      setMonth(11);
    } else {
      setMonth(m => m - 1);
    }
  };

  const goNext = () => {
    if (month === 11) {
      setYear(y => y + 1);
      setMonth(0);
    } else {
      setMonth(m => m + 1);
    }
  };

  const isToday = (day: number) =>
    day === today.getDate() &&
    month === today.getMonth() &&
    year === today.getFullYear();

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Calendar"
        description="Upcoming earnings across your coverage"
      />

      <div className="flex-1 overflow-y-auto">
        <div className="w-full px-6 lg:px-8 py-8">
          {/* Month navigation */}
          <div className="mb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous month"
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-neutral-200 bg-surface text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Next month"
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-neutral-200 bg-surface text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
            >
              <ChevronRight />
            </button>
            <h2 className="text-lg font-semibold text-neutral-900">
              {MONTH_NAMES[month]} {year}
            </h2>
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
            )}
            <div className="ml-auto flex items-center gap-1.5 text-xs text-neutral-600">
              <span className="inline-block w-2 h-2 rounded-full bg-primary-500" />
              Earnings
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-danger-500 hover:text-danger-700 font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Calendar surface — weekday header + day grid share one Card, like
              the table-backed pages (Coverage, etc.). */}
          <Card variant="default">
            {/* Weekday header */}
            <div className="grid grid-cols-7 border-b border-neutral-200 bg-neutral-50">
              {WEEKDAYS.map(wd => (
                <div
                  key={wd}
                  className="px-2 py-2.5 text-center text-[11px] font-semibold text-neutral-500 uppercase tracking-wider"
                >
                  {wd}
                </div>
              ))}
            </div>

            {/* Empty state — distinguish "no earnings this month" from a still-loading grid. */}
            {!loading && events.length === 0 && (
              <div className="px-3 py-2 text-center text-xs text-neutral-400 border-b border-neutral-100">
                No earnings across your coverage this month.
              </div>
            )}

            {/* Day grid. Gridlines are drawn with per-cell right/bottom borders
                (plus the container's left edge) rather than a `gap-px` backing:
                a 1px grid gap collapses at fractional zoom / devicePixelRatio. */}
            <div className="grid grid-cols-7 border-l border-neutral-200">
              {cells.map((day, idx) => {
                if (day === null) {
                  return <div key={`blank-${idx}`} className="bg-neutral-50 min-h-[7rem] border-r border-b border-neutral-200" />;
                }
                const iso = toIsoDate(year, month, day);
                const dayEvents = eventsByDate.get(iso) ?? [];
                const visible = dayEvents.slice(0, MAX_VISIBLE_EVENTS);
                const overflow = dayEvents.length - visible.length;
                return (
                  <div key={iso} className="bg-surface min-h-[7rem] p-1.5 flex flex-col gap-1 border-r border-b border-neutral-200">
                    <div
                      className={`text-xs font-medium self-end w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday(day)
                          ? 'bg-primary-600 text-white'
                          : 'text-neutral-500'
                      }`}
                    >
                      {day}
                    </div>
                    <div className="flex flex-col gap-1">
                      {visible.map(ev => (
                        <div
                          key={ev.id}
                          title={ev.title}
                          className="rounded-md px-1.5 py-1 text-[11px] leading-tight bg-primary-100"
                        >
                          <div className="flex items-center gap-1">
                            <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500" aria-hidden="true" />
                            <span className="font-semibold truncate text-primary-700">
                              {ev.assetUid}
                            </span>
                            {ev.reportingTime && (
                              <span
                                className="ml-auto shrink-0 text-[10px] font-medium uppercase text-primary-700/70"
                                title={`Reporting time: ${ev.reportingTime}`}
                              >
                                {ev.reportingTime}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-primary-700/70">{ev.title}</div>
                          {!ev.confirmed && (
                            <div
                              className="text-[10px] italic text-primary-700/70"
                              title="Tentative — date not yet confirmed"
                            >
                              tentative
                            </div>
                          )}
                        </div>
                      ))}
                      {overflow > 0 && (
                        <div
                          className="px-1.5 text-[10px] font-medium text-neutral-500"
                          title={dayEvents
                            .slice(MAX_VISIBLE_EVENTS)
                            .map(e => e.title)
                            .join('\n')}
                        >
                          +{overflow} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
